import * as THREE from 'three'
import { useRef, useMemo, useState, useEffect } from 'react'
import { useFrame, extend } from '@react-three/fiber'
import { shaderMaterial } from '@react-three/drei'
import { useStatic } from './context/StaticContext'
import { useCurrentAccount } from '@mysten/dapp-kit'

// Custom shader for the energy ball
const EnergyMaterial = shaderMaterial(
  {
    time: 0,
    color: new THREE.Color(1.0, 1.0, 0.0), // Default yellow color
    clickEffect: 0.0,
    explosionProgress: 0.0, // New uniform for explosion effect
  },
  // Vertex shader
  `
    varying vec2 vUv;
    varying vec3 vPosition;
    uniform float clickEffect;
    uniform float explosionProgress;
    
    void main() {
      vUv = uv;
      // Add explosion effect to vertex positions
      vec3 explodedPosition = position * (1.0 + explosionProgress * 2.0);
      vPosition = explodedPosition * (1.0 + clickEffect * 0.2);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(vPosition, 1.0);
    }
  `,
  // Fragment shader
  `
    uniform float time;
    uniform vec3 color;
    uniform float clickEffect;
    uniform float explosionProgress;
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
      // Simplified electric/plasma effect
      float noise = sin(vPosition.x * 5.0 + time) * cos(vPosition.y * 5.0 + time);
      noise = abs(noise);
      
      // Simplified pulsing glow
      float pulse = (sin(time) + 1.0) * 0.5;
      
      // Simplified electric edges
      float edge = abs(sin(vPosition.x * 10.0 + time * 2.0));
      edge = smoothstep(0.3, 0.7, edge);
      
      // Simplified effects
      vec3 finalColor = color * (noise * 0.5 + 0.5) * (pulse * 0.3 + 0.7);
      finalColor += color * edge * 0.3;
      
      // Fade out during explosion
      float alpha = 0.8;
      alpha *= (1.0 - explosionProgress);
      
      gl_FragColor = vec4(finalColor, alpha);
    }
  `
)

// Extend Three.js with our custom material
extend({ EnergyMaterial })

export function EnergyBall({ onBallClick }) {
  const meshRef = useRef()
  const materialRef = useRef()
  const { videoMode, textMode, setVideoMode, setTextMode, setStaticMode, awakeningMode } = useStatic()
  const [clickEffect, setClickEffect] = useState(0)
  const [clickCount, setClickCount] = useState(0)
  const [isExploding, setIsExploding] = useState(false)
  const [explosionProgress, setExplosionProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(true)
  const account = useCurrentAccount()
  
  // Physics state
  const physics = useRef({
    position: new THREE.Vector3(0, 2, 0),
    velocity: new THREE.Vector3(1.5, 2, 1),
    bounds: new THREE.Vector3(5, 4, 5),
    damping: 0.99,
    speedFactor: 0.5
  })

  // Update color based on awakening mode
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.color = new THREE.Color(awakeningMode ? 1.0 : 1.0, awakeningMode ? 0.0 : 1.0, 0.0)
    }
  }, [awakeningMode])

  const handleClick = (e) => {
    // Prevent clicking if wallet isn't connected or during awakening mode
    if (!account || awakeningMode) return
    
    if (!isExploding) {
      const newClickCount = clickCount + 1
      setClickCount(newClickCount)
      
      // Toggle video/text mode
      if (videoMode) {
        setVideoMode(false)
        setTextMode(true)
      } else {
        setVideoMode(true)
        setTextMode(false)
      }
      
      setClickEffect(1)
      
      const burst = 1
      physics.current.velocity.x += (Math.random() - 0.5) * burst
      physics.current.velocity.y += (Math.random() - 0.5) * burst
      physics.current.velocity.z += (Math.random() - 0.5) * burst

      // Check if we reached 10 clicks
      if (newClickCount >= 10) {
        setIsExploding(true)
        setStaticMode(true)
        // Only trigger clap when ball explodes
        onBallClick()
      }
    }
  }

  // Handle explosion and respawn
  useEffect(() => {
    if (isExploding) {
      // Hide the ball after explosion animation
      const explosionTimer = setTimeout(() => {
        setIsVisible(false)
        setExplosionProgress(0)
        setIsExploding(false)
        
        // Respawn after 30 seconds
        const respawnTimer = setTimeout(() => {
          setClickCount(0)
          setIsVisible(true)
          setStaticMode(false)
        }, 30000)
        
        return () => clearTimeout(respawnTimer)
      }, 2000) // Explosion animation duration
      
      return () => clearTimeout(explosionTimer)
    }
  }, [isExploding])

  // Update position and handle bouncing
  useFrame((state, delta) => {
    if (meshRef.current && materialRef.current) {
      if (isExploding) {
        // Update explosion progress
        setExplosionProgress(prev => Math.min(1, prev + delta))
      }
      
      const { position, velocity, bounds, damping, speedFactor } = physics.current
      
      // Only update position if not exploding
      if (!isExploding) {
        position.add(velocity.clone().multiplyScalar(delta * speedFactor))
        
        // Check boundaries and bounce
        if (Math.abs(position.x) > bounds.x) {
          position.x = Math.sign(position.x) * bounds.x
          velocity.x *= -damping
        }
        if (Math.abs(position.y) > bounds.y) {
          position.y = Math.sign(position.y) * bounds.y
          velocity.y *= -damping
        }
        if (Math.abs(position.z) > bounds.z) {
          position.z = Math.sign(position.z) * bounds.z
          velocity.z *= -damping
        }
      }
      
      // Apply position to mesh
      meshRef.current.position.copy(position)
      
      // Update shader uniforms
      materialRef.current.time = state.clock.elapsedTime
      materialRef.current.clickEffect = clickEffect
      materialRef.current.explosionProgress = explosionProgress
      
      // Fade out click effect
      if (clickEffect > 0) {
        setClickEffect(Math.max(0, clickEffect - delta * 2))
      }
      
      // Add some wobble to the ball when not exploding
      if (!isExploding) {
        meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime) * 0.2
        meshRef.current.rotation.z = Math.cos(state.clock.elapsedTime) * 0.2
      }
    }
  })

  if (!isVisible) return null

  return (
    <mesh 
      ref={meshRef} 
      scale={0.225}
      onClick={handleClick}
      style={{ cursor: account && !awakeningMode ? 'pointer' : 'default' }}
    >
      <sphereGeometry args={[1, 16, 16]} />
      <energyMaterial ref={materialRef} transparent depthWrite={false} />
      <pointLight 
        color={awakeningMode ? [1, 0, 0] : [1, 1, 0]} 
        intensity={1 + clickEffect + explosionProgress * 2} 
        distance={2 + clickEffect * 2 + explosionProgress * 4} 
      />
    </mesh>
  )
} 